import { ChartKind, type ChartWidgetProps } from '@fruitful/types';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { type LayoutChangeEvent, Platform, View as RNView } from 'react-native';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    type ColorTokenKey,
    useTheme,
    useThemeColors,
    YStack,
} from 'smoothie';

import { CHAT_WIDGET_MAX_WIDTH } from '../../layout/ChatLayout';
import { ChartLegend, type ChartLegendItem } from './ChartLegend';
import { DONUT_PALETTE } from './chartPalettes';
import { formatValue } from './chartUtils';
import { DonutChart, type SliceColors } from './DonutChart';
import { HorizontalBarChart } from './HorizontalBarChart';
import { LineAreaChart } from './LineAreaChart';
import { VerticalBarChart } from './VerticalBarChart';

const PRESSABLE_SELECTOR = '[tabindex="0"]';
/** Donut slices are SVG shapes carrying their own press handlers. */
const DONUT_SLICE_SELECTORS = ['svg path', 'svg circle'];

const webChartCursorCss = (domId: string, selectors: string[]): string => {
    const pointerRules = selectors
        .flatMap((selector) => [
            `#${domId} ${selector}`,
            `#${domId} ${selector} *`,
        ])
        .join(',\n');

    return `
#${domId},
#${domId} * {
  cursor: default !important;
}
${pointerRules} {
  cursor: pointer !important;
}
`;
};

const applyWebChartCursors = (root: HTMLElement, selectors: string[]) => {
    root.querySelectorAll<HTMLElement | SVGElement>('*').forEach((element) => {
        element.style.setProperty('cursor', 'default', 'important');
    });
    selectors.forEach((selector) => {
        root.querySelectorAll<HTMLElement | SVGElement>(selector).forEach(
            (element) => {
                element.style.setProperty('cursor', 'pointer', 'important');
                element
                    .querySelectorAll<HTMLElement | SVGElement>('*')
                    .forEach((child) => {
                        child.style.setProperty(
                            'cursor',
                            'pointer',
                            'important',
                        );
                    });
            },
        );
    });
};

export interface ChartSelectEvent {
    isLegendSelect: boolean;
    chartKind: ChartKind;
    chartSeriesCount: number;
}

interface ChartWidgetComponentProps extends ChartWidgetProps {
    onSelect?: (event: ChartSelectEvent) => void;
}

export const ChartWidget = ({
    kind,
    orientation,
    title,
    subtitle,
    series,
    valueFormat,
    showLegend,
    onSelect,
}: ChartWidgetComponentProps) => {
    const { getColor } = useThemeColors();
    const theme = useTheme();
    const [measuredWidth, setMeasuredWidth] = useState<number>();
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const chartCanvasDomId = `fruitful-chart-${useId().replace(/:/g, '')}`;
    const rawKind =
        typeof kind === 'string' ? kind.toLowerCase() : String(kind);
    const normalizedKind =
        rawKind === 'pie' ? ChartKind.Donut : (rawKind as ChartKind);
    const normalizedOrientation = orientation ?? 'vertical';
    const primary = series[0];

    const handleLayout = useCallback((event: LayoutChangeEvent) => {
        const nextWidth = event.nativeEvent.layout.width;
        setMeasuredWidth((previous) =>
            previous === nextWidth ? previous : nextWidth,
        );
    }, []);

    const chartWidth =
        measuredWidth != null && measuredWidth > 0
            ? Math.min(measuredWidth, CHAT_WIDGET_MAX_WIDTH)
            : undefined;
    const resolveThemeColor = useCallback(
        (token: ColorTokenKey): string => {
            const color: unknown = getColor(token);
            return typeof color === 'string' ? color : '';
        },
        [getColor],
    );

    const resolveSliceColors = useCallback(
        (
            pointColor: string | undefined,
            seriesColor: string | undefined,
            index: number,
        ): SliceColors => {
            const themeColors = theme as unknown as Record<string, unknown>;
            const isValidToken = (
                value: string | undefined,
            ): value is ColorTokenKey =>
                typeof value === 'string' &&
                value.startsWith('$') &&
                themeColors[value] != null;
            const token =
                (isValidToken(pointColor)
                    ? pointColor
                    : isValidToken(seriesColor)
                      ? seriesColor
                      : DONUT_PALETTE[index % DONUT_PALETTE.length]) ?? '$leaf';
            const fill = resolveThemeColor(token);
            // The selection outline is the darker shade of the slice colour.
            const outlineToken = `${token}Dark`;

            return {
                fill,
                outline: isValidToken(outlineToken)
                    ? resolveThemeColor(outlineToken)
                    : fill,
            };
        },
        [resolveThemeColor, theme],
    );

    const handleSelectIndex = useCallback(
        (index: number, isLegendSelect: boolean = false) => {
            setSelectedIndex((previous) => (previous === index ? null : index));
            onSelect?.({
                isLegendSelect,
                chartKind: normalizedKind,
                chartSeriesCount: series.length,
            });
        },
        [normalizedKind, onSelect, series.length],
    );

    useEffect(() => {
        setSelectedIndex(null);
    }, [kind, orientation, series]);

    const pointerSelectors = useMemo(
        () =>
            normalizedKind === ChartKind.Donut
                ? [PRESSABLE_SELECTOR, ...DONUT_SLICE_SELECTORS]
                : [PRESSABLE_SELECTOR],
        [normalizedKind],
    );

    useEffect(() => {
        if (Platform.OS !== 'web') {
            return;
        }

        let cancelled = false;
        let observer: MutationObserver | null = null;
        let animationFrameId = 0;

        const setup = () => {
            const root = document.getElementById(chartCanvasDomId);
            if (root == null) {
                animationFrameId = requestAnimationFrame(setup);
                return;
            }
            if (cancelled) {
                return;
            }

            applyWebChartCursors(root, pointerSelectors);
            observer = new MutationObserver(() => {
                applyWebChartCursors(root, pointerSelectors);
            });
            observer.observe(root, { childList: true, subtree: true });
        };

        setup();

        return () => {
            cancelled = true;
            cancelAnimationFrame(animationFrameId);
            observer?.disconnect();
        };
    }, [chartCanvasDomId, chartWidth, pointerSelectors, series]);

    const legendItems = useMemo<ChartLegendItem[]>(() => {
        if (primary == null) {
            return [];
        }

        if (normalizedKind === ChartKind.Donut) {
            return primary.points.map((point, index) => ({
                color: resolveSliceColors(point.color, primary.color, index)
                    .fill,
                label: point.label,
                value: formatValue(point.value, valueFormat),
            }));
        }

        if (normalizedKind === ChartKind.Line && showLegend === true) {
            return [
                {
                    color: resolveThemeColor('$fruitfulGreen'),
                    label: primary.name ?? 'Series 1',
                    value: '',
                },
            ];
        }

        return [];
    }, [
        normalizedKind,
        primary,
        resolveSliceColors,
        resolveThemeColor,
        showLegend,
        valueFormat,
    ]);

    if (primary == null || primary.points.length === 0) {
        return null;
    }

    const shouldShowLegend =
        normalizedKind === ChartKind.Donut
            ? (showLegend ?? true)
            : showLegend === true && legendItems.length > 0;

    return (
        <YStack width="100%" gap="$2">
            {title != null && title !== '' ? (
                <BodyMediumEmphasized color="$black">
                    {title}
                </BodyMediumEmphasized>
            ) : null}
            {subtitle != null && subtitle !== '' ? (
                <BodySmallRegular color="$gray1000">
                    {subtitle}
                </BodySmallRegular>
            ) : null}
            {Platform.OS === 'web' ? (
                <style
                    dangerouslySetInnerHTML={{
                        __html: webChartCursorCss(
                            chartCanvasDomId,
                            pointerSelectors,
                        ),
                    }}
                />
            ) : null}
            <RNView
                {...(Platform.OS === 'web' ? { id: chartCanvasDomId } : {})}
                onLayout={handleLayout}
                style={{
                    width: '100%',
                    alignItems: 'center',
                    paddingVertical: 8,
                }}
            >
                {chartWidth != null ? (
                    normalizedKind === ChartKind.Donut ? (
                        <DonutChart
                            series={primary}
                            valueFormat={valueFormat}
                            chartWidth={chartWidth}
                            resolveSliceColors={resolveSliceColors}
                            selectedIndex={selectedIndex}
                            onSelectIndex={(index) => {
                                handleSelectIndex(index);
                            }}
                        />
                    ) : normalizedKind === ChartKind.Line ? (
                        <LineAreaChart
                            series={primary}
                            valueFormat={valueFormat}
                            chartWidth={chartWidth}
                            getColor={resolveThemeColor}
                        />
                    ) : normalizedOrientation === 'horizontal' ? (
                        <HorizontalBarChart
                            series={primary}
                            valueFormat={valueFormat}
                            getColor={resolveThemeColor}
                        />
                    ) : (
                        <VerticalBarChart
                            series={primary}
                            valueFormat={valueFormat}
                            chartWidth={chartWidth}
                            getColor={resolveThemeColor}
                        />
                    )
                ) : null}
            </RNView>
            {shouldShowLegend ? (
                <ChartLegend
                    items={legendItems}
                    selectedIndex={
                        normalizedKind === ChartKind.Donut
                            ? selectedIndex
                            : null
                    }
                    onSelectIndex={
                        normalizedKind === ChartKind.Donut
                            ? (index) => {
                                  handleSelectIndex(index, true);
                              }
                            : undefined
                    }
                />
            ) : null}
        </YStack>
    );
};
